import type { Steps } from "@/types/global";
import type { UserDetails } from "@/types/user";
import type { VerificationField, VerificationFieldState } from "@/types/register";
import { useStorage } from "@vueuse/core";
import { JOIN_CODE_MAX_LENGTH } from "~/constants/general";

const makeVerificationField = (
  field: VerificationField,
  channel: 'email' | 'sms',
): VerificationFieldState => ({
  field,
  channel,
  target: '',
  signupId: '',
  codeSent: false,
  verified: false,
  isSending: false,
  isVerifying: false,
  cooldownRemaining: 0,
  otpCode: '',
  error: null,
});

export const useRegister = defineStore("register", () => {
  const route = useRoute();
  const steps = ref<Steps>({
    step: 1,
    route: "",
    noService: false,
  });

  const stepHistory = ref<string[]>([]);

  const trial = useStorage(
    "",
    ref({
      trial_period_token: "",
    }),
  );

  const userDetails = ref<UserDetails>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_country_code: "",
    otp_type: "",
    password: "",
    password_confirmation: "",
    id: "",
    secondary_email: "",
  });

  const selectedState = ref("");

  const acknowledgments = reactive({
    isNotary: false,
    understandSoftware: false,
    noExistingLogin: false,
  });

  const verificationFields = reactive<Record<VerificationField, VerificationFieldState>>({
    email: makeVerificationField('email', 'email'),
    secondary_email: makeVerificationField('secondary_email', 'email'),
    phone: makeVerificationField('phone', 'sms'),
  });

  const resetVerificationFields = () => {
    verificationFields.email = makeVerificationField('email', 'email');
    verificationFields.secondary_email = makeVerificationField('secondary_email', 'email');
    verificationFields.phone = makeVerificationField('phone', 'sms');
  };

  const navigate = (path: string, options?: Parameters<typeof navigateTo>[1]) => {
    const query: Record<string, string> = {};

    const joinCode = route.query.join_code as string | undefined;
    if (joinCode && joinCode.length <= JOIN_CODE_MAX_LENGTH) {
      query.join_code = joinCode;
    }

    // Ad attribution has to survive the step transitions, or a visitor who arrived
    // from an ad reaches account creation with a bare URL. The durable copy is the
    // `localStorage` snapshot in `utils/adAttribution`; keeping the parameters on
    // the URL as well means the fallback still works when that is unavailable.
    for (const key of ATTRIBUTION_PARAM_KEYS) {
      const value = route.query[key];
      if (typeof value === "string" && value !== "") {
        query[key] = value;
      }
    }

    if (Object.keys(query).length > 0) {
      return navigateTo({ path, query }, options);
    }

    return navigateTo(path, options);
  };

  const clearState = () => {
    steps.value = {
      step: 0,
      route: "",
      noService: false,
    };

    userDetails.value = {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      phone_country_code: "",
      otp_type: "",
      secondary_email: "",
      password: "",
    };

    selectedState.value = "";
    acknowledgments.isNotary = false;
    acknowledgments.understandSoftware = false;
    acknowledgments.noExistingLogin = false;
    resetVerificationFields();
  };

  return { steps, userDetails, trial, stepHistory, selectedState, verificationFields, acknowledgments, resetVerificationFields, clearState, navigate };
});
